import { Check, ChevronDown, Save, Star, StarOff, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, Input } from '@/components/ui/field';
import { useCreateView, useDeleteView, useUpdateView, type SavedView } from '@/data/views';
import { cn } from '@/lib/utils';
import { toJson, type GridViewConfig } from './views';

/**
 * Grid's saved views: pick one, save what's on screen as a new view or into
 * the one in use, and choose which opens by default. Each founder has their own.
 * Views ticked "show in bar" also sit as one-click buttons left of the menu.
 */
export function SavedViews({
  views,
  active,
  modified,
  current,
  onApply,
  onActivate,
}: {
  views: readonly SavedView[];
  /** The view in use, if any. */
  active: SavedView | undefined;
  /** The screen no longer matches the view in use. */
  modified: boolean;
  current: GridViewConfig;
  onApply: (view: SavedView) => void;
  /** A view was just saved: it's now the one in use. */
  onActivate: (view: SavedView) => void;
}) {
  const update = useUpdateView('grid');
  const remove = useDeleteView('grid');
  const [saving, setSaving] = useState(false);

  const inBar = views.filter((v) => v.show_in_bar);

  return (
    <div className="flex min-w-0 items-center gap-2">
      {inBar.length ? (
        // Hidden at phone width, where the menu alone does the job.
        <div role="group" aria-label="View bar" className="hidden min-w-0 gap-1 overflow-x-auto md:flex">
          {inBar.map((v) => (
            <button
              key={v.id}
              type="button"
              aria-pressed={v.id === active?.id}
              onClick={() => onApply(v)}
              title={v.is_default ? `${v.name} (opens by default)` : v.name}
              className={cn(
                'flex max-w-40 shrink-0 cursor-pointer items-center gap-1 px-2.5 py-1 font-display text-[10px] font-bold tracking-[0.12em] uppercase shadow-[inset_0_0_0_1px_var(--color-line-hot)]',
                v.id === active?.id
                  ? 'bg-cyan/15 text-cyan shadow-[inset_0_0_0_1px_var(--color-cyan)]'
                  : 'text-muted hover:text-text',
              )}
            >
              {v.is_default ? <Star className="size-3 shrink-0 text-yellow" aria-hidden /> : null}
              <span className="truncate">{v.name}</span>
            </button>
          ))}
        </div>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" aria-label="Saved views">
            <span className="max-w-60 truncate normal-case">
              {active ? `${active.name}${modified ? ' (edited)' : ''}` : 'Views'}
            </span>
            <ChevronDown aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-64">
          <DropdownMenuLabel className="font-display text-[9px] font-bold tracking-[0.16em] text-muted uppercase">
            My views
          </DropdownMenuLabel>
          {views.map((v) => (
            <DropdownMenuItem key={v.id} onSelect={() => onApply(v)}>
              <Check className={cn('size-3.5', v.id === active?.id ? 'text-cyan' : 'invisible')} aria-hidden />
              <span className="flex-1 truncate">{v.name}</span>
              {v.is_default ? <Star className="size-3.5 text-yellow" aria-label="Default" /> : null}
            </DropdownMenuItem>
          ))}
          {views.length === 0 ? (
            <p className="px-2.5 py-1.5 text-xs text-dim">None yet. Set up the view and filters, then save it.</p>
          ) : null}
          {views.length ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="font-display text-[9px] font-bold tracking-[0.16em] text-muted uppercase">
                Show in bar
              </DropdownMenuLabel>
              {views.map((v) => (
                <DropdownMenuCheckboxItem
                  key={v.id}
                  checked={v.show_in_bar}
                  onCheckedChange={(checked) => update.mutate({ id: v.id, patch: { show_in_bar: checked } })}
                >
                  <span className="truncate">{v.name}</span>
                </DropdownMenuCheckboxItem>
              ))}
            </>
          ) : null}
          <DropdownMenuSeparator />
          {active && modified ? (
            <DropdownMenuItem onSelect={() => update.mutate({ id: active.id, patch: { config: toJson(current) } })}>
              <Save aria-hidden /> Save changes to “{active.name}”
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={() => setSaving(true)}>
            <Save aria-hidden /> Save as a new view…
          </DropdownMenuItem>
          {active ? (
            <>
              <DropdownMenuItem
                onSelect={() => update.mutate({ id: active.id, patch: { is_default: !active.is_default } })}
              >
                {active.is_default ? <StarOff aria-hidden /> : <Star aria-hidden />}
                {active.is_default ? 'Stop opening it by default' : `Open “${active.name}” by default`}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red"
                onSelect={() => {
                  if (window.confirm(`Delete the view “${active.name}”?`)) remove.mutate(active.id);
                }}
              >
                <Trash2 aria-hidden /> Delete “{active.name}”
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {saving ? <SaveViewDialog current={current} onClose={() => setSaving(false)} onSaved={onActivate} /> : null}
    </div>
  );
}

function SaveViewDialog({
  current,
  onClose,
  onSaved,
}: {
  current: GridViewConfig;
  onClose: () => void;
  onSaved: (view: SavedView) => void;
}) {
  const create = useCreateView('grid');
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [showInBar, setShowInBar] = useState(true);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title="Save this view">
        <DialogDescription className="text-sm text-muted">
          Saves the view type ({current.view}), the filters and the sort. Only you see your views.
        </DialogDescription>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(
              { name, config: toJson(current), is_default: isDefault, show_in_bar: showInBar },
              {
                onSuccess: (view) => {
                  onSaved(view);
                  onClose();
                },
              },
            );
          }}
        >
          <Field label="Name">
            <Input
              autoFocus
              required
              maxLength={60}
              value={name}
              placeholder="e.g. Board, current sprint"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-cyan"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            Open Grid in this view by default
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-cyan"
              checked={showInBar}
              onChange={(e) => setShowInBar(e.target.checked)}
            />
            Show it in the view bar
          </label>
          {create.error ? (
            <p role="alert" className="font-mono text-xs text-red">
              {create.error.message}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              Save view
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
